export type NumericMode = "integer" | "decimal";

export interface NumericFilterOptions {
    /** "integer" allows digits only, "decimal" allows a single decimal point. Default "decimal". */
    mode?: NumericMode;
    /** Max digits allowed after the decimal point in "decimal" mode. Default 2 (money). */
    decimalPlaces?: number;
    /** Allow a leading minus sign. Default false. */
    allowNegative?: boolean;
    /** Optional inclusive upper bound (e.g. 100 for a percentage field). Values above it are clamped. */
    max?: number;
    /** Optional max character length of the resulting string. */
    maxLength?: number;
}

const DEFAULT_DECIMAL_PLACES = 2;

/**
 * Strips a raw string down to the characters allowed by the given numeric mode,
 * preserving partial-typing states (e.g. "-", "1.", "") so it's safe to call on
 * every keystroke. Never throws.
 */
export function sanitizeNumericString(raw: string, opts: NumericFilterOptions = {}): string {
    const {
        mode = "decimal",
        decimalPlaces = DEFAULT_DECIMAL_PLACES,
        allowNegative = false,
        max,
        maxLength,
    } = opts;

    if (raw === "") {
        return "";
    }

    let negative = false;
    let rest = raw;
    if (allowNegative && rest.trim().startsWith("-")) {
        negative = true;
    }
    rest = rest.replace(/-/g, "");

    let result: string;
    if (mode === "integer" || decimalPlaces <= 0) {
        result = rest.replace(/\D/g, "");
    } else {
        const dotIndex = rest.indexOf(".");
        if (dotIndex === -1) {
            result = rest.replace(/\D/g, "");
        } else {
            const intPart = rest.slice(0, dotIndex).replace(/\D/g, "");
            const fracPart = rest.slice(dotIndex + 1).replace(/\D/g, "").slice(0, decimalPlaces);
            result = `${intPart}.${fracPart}`;
        }
    }

    if (negative) {
        result = result === "" ? "-" : `-${result}`;
    }

    if (typeof max === "number" && result !== "" && result !== "-") {
        const numeric = Number(result);
        if (Number.isFinite(numeric) && Math.abs(numeric) > max) {
            result = negative ? `-${max}` : String(max);
        }
    }

    if (typeof maxLength === "number") {
        result = result.slice(0, maxLength);
    }

    return result;
}

/** True if `value` is already a fully-sanitized numeric string under the given options. */
export function isValidNumericString(value: string, opts: NumericFilterOptions = {}): boolean {
    if (value === "") {
        return true;
    }
    return sanitizeNumericString(value, opts) === value;
}

/**
 * Convenience onChange factory for raw <input> elements: filters the incoming
 * value through sanitizeNumericString and forwards the sanitized result.
 */
export function createNumericChangeHandler(
    setValue: (next: string) => void,
    opts: NumericFilterOptions = {},
): (value: string) => void {
    return (value: string) => {
        setValue(sanitizeNumericString(value, opts));
    };
}

/** True if a numeric string represents zero (e.g. "0", "0.00", "-0"). Empty strings are not zero-like. */
export function isZeroNumericString(value: string): boolean {
    if (value.trim() === "") {
        return false;
    }
    const parsed = Number(value);
    return Number.isFinite(parsed) && parsed === 0;
}
